import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgProgressModule } from 'ngx-progressbar';
import { SharedModule } from '@app/shared/shared.module';
import { AppComponent } from '@app/app.component';
import { KeycloakService, KeycloakAngularModule } from 'keycloak-angular';
import { AppRoutingModule } from './app-routing.module';
import { DatePipe } from '@angular/common';
import { CoreModule } from '@app/core/core.module';
import { ConfigurationService } from './core/services/configuration/configuration.service';
import { AdministratorService } from './core/services/administrator/administrator.service';
import { ModuleConfigurationService } from './core/services/module-configuration/module-configuration.service';
import { RoleService } from './core/services/administrator/role.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { catchError, lastValueFrom, of } from 'rxjs';
import { MaintenanceBannerComponent } from './shared/components/maintenance-banner/maintenance-banner.component';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    MatSnackBarModule,
    AppRoutingModule,
    CoreModule.forRoot(),
    SharedModule.forRoot(),
    BrowserAnimationsModule,
    NgProgressModule.withConfig({
      spinner: false,
      color: '#1abb9c',
      thick: true
    }),
    TranslateModule.forRoot({
      fallbackLang: 'fr',
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json'
      })
    }),
    KeycloakAngularModule,
    MaintenanceBannerComponent
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = (init)(inject(KeycloakService), inject(ConfigurationService), inject(AdministratorService), inject(ModuleConfigurationService), inject(RoleService));
      return initializerFn();
    }),
    DatePipe,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}

/**
 * Init function
 * The promises will be played before the app (preloading).
 *
 * @param keycloak
 * @param configurationService
 * @returns
 */
export function init(keycloak: KeycloakService, configurationService: ConfigurationService, administratorService: AdministratorService, moduleConfigurationService: ModuleConfigurationService, roleService: RoleService): () => Promise<any> {
  return async (): Promise<any> => {
    // Fetch local configuration
    const environment = await lastValueFrom(configurationService.refreshConfiguration());

    // Init keycloak
    await keycloak.init({
      config: environment.keycloak,
      loadUserProfileAtStartUp: false,
      initOptions: {
        onLoad: 'login-required',
        checkLoginIframe: false
      },
      bearerExcludedUrls: [
        '/maintenance'
      ]
    });

    // Preload custom language translation
    await configurationService.addCustomTranslation();

    // Load roles informations of configuration
    await lastValueFrom(roleService.getRolesConfiguration());

    // Load mobilityAccountManager configuration
    await lastValueFrom(moduleConfigurationService.getMobilityAccountManagerConfiguration().pipe(catchError(_err => { return of(null) })));

    // Save logged user (usefull to get permissions and role)
    await lastValueFrom(administratorService.refreshLoggedAgent());

    return true;
  };
}
